import { initializeApp } from "firebase/app";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { initializeFirestore, persistentLocalCache, persistentMultipleTabManager, connectFirestoreEmulator, terminate, clearIndexedDbPersistence } from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";

export const firebaseConfig = {
  apiKey: (import.meta as any).env?.VITE_FIREBASE_API_KEY || "AIzaSyC5N8t29kDuVZEPzp9Ss5cah8KCcpPdmgE",
  authDomain: (import.meta as any).env?.VITE_FIREBASE_AUTH_DOMAIN || "kca-portal.firebaseapp.com",
  databaseURL: "https://kca-portal-default-rtdb.firebaseio.com",
  projectId: "kca-portal",
  storageBucket: "kca-portal.firebasestorage.app",
  messagingSenderId: "62562721746",
  appId: "1:62562721746:web:45d66853351e897464720b"
};

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

// Offline persistence (IndexedDB-backed): cached reads survive reload/no-network,
// and writes queue locally and sync automatically on reconnect. Multi-tab manager
// so a second open tab doesn't silently fall back to memory-only caching.
// NOTE: this is the only initializeFirestore/getFirestore call in the app — every
// service file imports `db` from here, so it must run before anything else touches
// Firestore, and must never be called a second time for this app instance.
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
});

// Recovery path for a wedged local Firestore cache: once the SDK hits an internal
// assertion failure (a real, if rare, bug class in persistentLocalCache — see the
// "FIRESTORE ... INTERNAL ASSERTION FAILED" crash this was built for), the corruption
// lives in this device's own IndexedDB and survives a plain reload — the fix has to
// actually clear the on-disk cache, not just reload fresh JS. clearIndexedDbPersistence
// requires the instance to not be actively in use, hence terminate() first. Always ends
// in a reload (even the fallback path), since there's no good in-app state to return to
// once the local cache is gone from under every existing listener.
export const clearFirestoreCacheAndReload = async (redirectTo?: string) => {
  try {
    await terminate(db);
    await clearIndexedDbPersistence(db);
  } catch (e) {
    console.error('clearFirestoreCacheAndReload: SDK-level clear failed, falling back to raw IndexedDB deletion', e);
    try {
      const dbs = await indexedDB.databases?.();
      await Promise.all(
        (dbs || [])
          .filter((d) => (d.name || '').toLowerCase().includes('firestore'))
          .map((d) => new Promise<void>((resolve) => {
            const req = indexedDB.deleteDatabase(d.name!);
            req.onsuccess = () => resolve();
            req.onerror = () => resolve();
            req.onblocked = () => resolve();
          }))
      );
    } catch (e2) {
      console.error('clearFirestoreCacheAndReload: raw IndexedDB deletion also failed', e2);
    }
  } finally {
    if (redirectTo) window.location.href = redirectTo;
    else window.location.reload();
  }
};

// Storage may not be enabled yet in the Firebase Console — initialise lazily
// so a missing bucket doesn't crash the whole app on load.
let _storage: ReturnType<typeof getStorage> | null = null;
try {
  _storage = getStorage(app);
} catch (e) {
  console.warn('Firebase Storage not available:', e);
}
export const storage = _storage;

// Opt-in only, never active unless explicitly requested — points this app instance
// at local Firebase Emulators instead of the real kca-portal project, so testing
// (new roles, seeded fixtures, destructive rules experiments) never touches real
// school data. Must run before anything else touches auth/db/storage, hence right
// here at the bottom of this module rather than in a separate file.
if ((import.meta as any).env?.VITE_USE_EMULATOR === 'true') {
  console.warn('%c[kca-portal] Connected to LOCAL FIREBASE EMULATORS — not production.', 'color: orange; font-weight: bold;');
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  if (_storage) connectStorageEmulator(_storage, '127.0.0.1', 9199);
}
